import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMemberUna } from "@/hooks/useMemberUna";
import { downloadDocument } from "@/lib/documentDownload";
import { humanTaskStatus } from "@/lib/humanTaskStatus";

type TaskKey = "irs_contact_update" | "coding_letter";

const TASKS: {
  key: TaskKey;
  title: string;
  body: string;
  documentType: string;
  confirmation: string;
  journalTitle: string;
}[] = [
  {
    key: "irs_contact_update",
    title: "Put your name on file with the IRS",
    body: "You run your Association. The IRS file should show your name as its contact, not ours. Download the form below. Almost every line is filled in. Add your Social Security number on the marked line. We never see that number. Sign as an officer, add your title, and mail it to the address on the instruction sheet. Then mark this task done and enter the date you mailed it.",
    documentType: "baton_pass_8822b",
    confirmation: "Done. Your name is on its way to the IRS file. The second task is now open.",
    journalTitle: "IRS contact update mailed",
  },
  {
    key: "coding_letter",
    title: "Send the coding letter",
    body: "This short letter asks the IRS to correct your Association's coding, so their computer stops expecting a yearly report form your Association does not file. The wording was prepared with licensed review. Please do not change it. Print it, sign as an officer, and mail it to the address shown. Keep a copy in The Record, then mark this task done.",
    documentType: "baton_pass_coding_letter",
    confirmation: "Done. Both steps are complete. Copies belong in The Record.",
    journalTitle: "Coding letter mailed",
  },
];

const LOCKED_COPY =
  "This task opens after the first task is done. The letter should come from the officer the IRS file knows, and after step one, that is you.";

export default function BatonPassTasks() {
  const { user } = useAuth();
  const { unaId } = useMemberUna();
  const queryClient = useQueryClient();
  const [dates, setDates] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const { data: flagOn = false, isLoading: flagLoading } = useQuery({
    queryKey: ["feature-flag", "baton_pass_enabled"],
    queryFn: async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("is_enabled")
        .eq("flag_key", "baton_pass_enabled")
        .maybeSingle();
      return !!data?.is_enabled;
    },
    staleTime: 60_000,
  });

  const { data: progress = [] } = useQuery({
    queryKey: ["baton-pass-progress", unaId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("baton_pass_progress")
        .select("task_key, status, mailed_date, completed_at")
        .eq("una_id", unaId!)
        .eq("user_id", user!.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!flagOn && !!unaId && !!user?.id,
  });

  const { data: docs = [] } = useQuery({
    queryKey: ["baton-pass-docs", unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generated_documents")
        .select("id, document_name, document_type, file_path, file_url")
        .eq("una_id", unaId!)
        .in("document_type", TASKS.map((t) => t.documentType));
      if (error) throw error;
      return data || [];
    },
    enabled: !!flagOn && !!unaId,
  });

  if (!flagOn || flagLoading || !unaId || !user?.id) return null;

  const rowFor = (key: TaskKey) => progress.find((p: any) => p.task_key === key);
  const isDone = (key: TaskKey) => rowFor(key)?.status === "completed";

  const handleDownload = async (documentType: string) => {
    const doc = docs.find((d: any) => d.document_type === documentType);
    if (!doc) return;
    setDownloadingKey(documentType);
    try {
      await downloadDocument(doc as any);
    } catch (err: any) {
      toast.error(err?.message || "That download did not go through. Please try again.");
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleComplete = async (task: (typeof TASKS)[number]) => {
    const mailed = dates[task.key];
    if (!mailed) {
      toast.error("Please enter the date you mailed it.");
      return;
    }
    setSavingKey(task.key);
    try {
      const { error } = await supabase.from("baton_pass_progress").upsert(
        {
          una_id: unaId,
          user_id: user.id,
          task_key: task.key,
          status: "completed",
          mailed_date: mailed,
          completed_at: new Date().toISOString(),
        },
        { onConflict: "una_id,user_id,task_key" },
      );
      if (error) throw error;

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();

      await supabase.from("una_journal_records").insert({
        una_id: unaId,
        record_type: "Activity Log Entry",
        record_date: mailed,
        record_title: task.journalTitle,
        activity_description: task.journalTitle,
        authorized_by: profile?.full_name || user.email || "Officer",
        created_by: user.id,
      });

      queryClient.invalidateQueries({ queryKey: ["baton-pass-progress", unaId, user.id] });
      toast.success(task.confirmation);
    } catch (err: any) {
      toast.error("We could not save that. Please try again.");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xl font-semibold">Baton Pass</h3>
        <p className="text-muted-foreground text-sm">
          Two short filings that move your Association's paperwork fully into your hands.
        </p>
      </div>

      {TASKS.map((task, index) => {
        const done = isDone(task.key);
        const locked = index === 1 && !isDone("irs_contact_update");
        const doc = docs.find((d: any) => d.document_type === task.documentType);
        const row = rowFor(task.key);

        return (
          <Card key={task.key} className={locked ? "opacity-90" : undefined}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {locked && <Lock className="w-4 h-4 text-muted-foreground shrink-0" />}
                    {done && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                    <span>{task.title}</span>
                  </CardTitle>
                  {locked && <CardDescription className="mt-2">{LOCKED_COPY}</CardDescription>}
                </div>
                <Badge variant={done ? "default" : "secondary"} className="shrink-0">
                  {locked ? "Locked" : humanTaskStatus(done ? "completed" : "not_started")}
                </Badge>
              </div>
            </CardHeader>

            {!locked && (
              <CardContent className="space-y-4">
                <p className="text-sm leading-relaxed">{task.body}</p>

                <div>
                  {doc ? (
                    <Button
                      variant="outline"
                      className="min-h-[44px]"
                      onClick={() => handleDownload(task.documentType)}
                      disabled={downloadingKey === task.documentType}
                    >
                      {downloadingKey === task.documentType ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download your document
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Your document is not ready yet. It will appear here as soon as it is prepared for your Association.
                    </p>
                  )}
                </div>

                {done ? (
                  <div className="rounded-lg border border-primary/40 bg-muted/40 p-3">
                    <p className="text-sm font-medium">{task.confirmation}</p>
                    {row?.mailed_date && (
                      <p className="text-xs text-muted-foreground mt-1">Date you mailed it: {row.mailed_date}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor={`mailed-${task.key}`}>Date you mailed it</Label>
                      <Input
                        id={`mailed-${task.key}`}
                        type="date"
                        className="min-h-[44px] mt-1"
                        value={dates[task.key] || ""}
                        onChange={(e) => setDates((prev) => ({ ...prev, [task.key]: e.target.value }))}
                      />
                    </div>
                    <Button
                      className="min-h-[44px]"
                      onClick={() => handleComplete(task)}
                      disabled={savingKey === task.key}
                    >
                      {savingKey === task.key && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Mark this task done
                    </Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
